// src/components/ErrorBoundary.tsx
//
// React error boundary. React only catches render-phase errors in a class
// component via getDerivedStateFromError / componentDidCatch — there is no hook
// equivalent. Without one, a single thrown render error unmounts the entire app
// and leaves a blank white screen (users reported this as "page urd jata hai").
// This contains the damage to a recoverable, on-brand fallback instead.
//
// Two modes:
//   • default (root)  — full-page fallback with a "Reload page" button. Used once,
//     wrapping <App/> in src/index.js.
//   • inline (local)  — a compact strip with a "Try again" button that resets the
//     boundary in place (no full reload). Use it to isolate a high-risk widget so
//     its failure doesn't take down the surrounding screen — e.g. the AI generate
//     button inside the task modal, the component behind the original crash.
//
// Callers can also pass a custom `fallback` (a node, or a render fn that receives
// the caught error + a reset() to clear it). The fallback is deliberately
// self-contained: it styles itself with the global Forest Sage tokens (var(--gt-*),
// defined on :root) plus the shared .gt-card / .gt-action-btn classes, and pulls in
// no hooks, context, or component imports — so it still renders when the rest of
// the app has fallen over.
import * as React from 'react';

type FallbackRender = (args: { error: Error; reset: () => void }) => React.ReactNode;

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback: a node, or a render fn receiving the caught error + reset(). */
  fallback?: React.ReactNode | FallbackRender;
  /** Render the compact inline fallback (for a widget/section) instead of full-page. */
  inline?: boolean;
  /** Label included in the console log so you can tell which boundary fired. */
  name?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Tagged (with the optional boundary name) so it's greppable in logs and easy
    // to wire to an error reporter later.
    const where = this.props.name ? ` (${this.props.name})` : '';
    console.error(`[ErrorBoundary]${where} Uncaught render error:`, error, info.componentStack);
  }

  // Clear the error so the children get another chance to render. Localized
  // boundaries surface this as "Try again"; if the same error recurs it simply
  // shows the fallback again.
  private reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { fallback, inline } = this.props;
    const error = this.state.error ?? new Error('Unknown error');

    if (fallback !== undefined) {
      return typeof fallback === 'function'
        ? (fallback as FallbackRender)({ error, reset: this.reset })
        : fallback;
    }
    return inline ? this.renderInlineFallback() : this.renderPageFallback();
  }

  private renderInlineFallback(): React.ReactNode {
    return (
      <span
        role="alert"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          borderRadius: 'var(--gt-radius-input, 14px)',
          background: 'var(--gt-prio-urgent-bg)',
          color: 'var(--gt-prio-urgent-fg)',
          fontFamily: 'var(--gt-font)',
          fontSize: '13px',
          fontWeight: 500,
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
        <span>Something went wrong.</span>
        <button
          type="button"
          onClick={this.reset}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 700,
            color: 'var(--gt-primary)',
            textDecoration: 'underline',
          }}
        >
          Try again
        </button>
      </span>
    );
  }

  private renderPageFallback(): React.ReactNode {
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--gt-surface-1)',
          fontFamily: 'var(--gt-font)',
          color: 'var(--gt-text-primary)',
        }}
      >
        <div
          className="gt-card"
          style={{ maxWidth: '420px', width: '100%', padding: '40px 32px', textAlign: 'center' }}
        >
          <div
            aria-hidden="true"
            style={{
              margin: '0 auto 20px',
              width: '52px',
              height: '52px',
              borderRadius: 'var(--gt-radius-pill, 999px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--gt-prio-urgent-bg)',
              color: 'var(--gt-dnd)',
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>

          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--gt-text-primary)' }}>
            Something went wrong
          </h1>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: '14px',
              lineHeight: 1.55,
              color: 'var(--gt-text-secondary)',
            }}
          >
            The page hit an unexpected error and couldn't finish loading. Reloading
            usually fixes it — if it keeps happening, please contact support.
          </p>

          <button
            type="button"
            onClick={this.handleReload}
            className="gt-action-btn"
            style={{ marginTop: '24px' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
